import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faSun, 
  faMoon, 
  faBars, 
  faTimes,
  faPaw,
  faHome,
  faBullseye,
  faHospital,
  faUser,
  faCalendarDays
} from '@fortawesome/free-solid-svg-icons';
import { useTheme } from '../contexts/ThemeContext';

function Header() {
  const { isDarkMode, toggleTheme } = useTheme();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();

  const navItems = [
    { path: '/', label: 'Home', icon: faHome },
    { path: '/quiz', label: 'Quiz', icon: faBullseye },
    { path: '/services', label: 'Services', icon: faHospital },
    { path: '/profile', label: 'Profile', icon: faUser },
    { path: '/playdates', label: 'Playdates', icon: faCalendarDays },
  ];

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  return (
    <header 
      className="bg-white dark:bg-neutral-800 shadow-lg border-b border-neutral-200 dark:border-neutral-700 sticky top-0 z-50 transition-colors"
      role="banner"
    >
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="flex h-16 items-center justify-between">
          {/* Logo */}
          <div className="flex items-center flex-shrink-0">
            <Link 
              to="/" 
              className="flex items-center space-x-2 text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300 transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 focus:ring-offset-white dark:focus:ring-offset-neutral-800 rounded"
              aria-label="Pawfect Match - Go to homepage"
            >
              <FontAwesomeIcon icon={faPaw} className="h-8 w-8" />
              <span className="text-xl font-bold">
                Pawfect Match
              </span>
            </Link>
          </div>

          {/* Mobile Menu Button - Always visible */}
          <div className="flex items-center space-x-4 flex-shrink-0">
            {/* Theme Toggle Button */}
            <button
              onClick={toggleTheme}
              className="p-2 text-neutral-800 hover:text-white hover:bg-primary-600 dark:text-neutral-100 dark:hover:text-white dark:hover:bg-primary-600 rounded-md transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 focus:ring-offset-white dark:focus:ring-offset-neutral-800"
              aria-label={isDarkMode ? 'Switch to light mode' : 'Switch to dark mode'}
              title={isDarkMode ? 'Switch to light mode' : 'Switch to dark mode'}
            >
              <FontAwesomeIcon 
                icon={isDarkMode ? faSun : faMoon} 
                className="h-5 w-5"
              />
            </button>

            {/* Mobile Menu Button */}
            <button
              onClick={toggleMobileMenu}
              className="p-2 text-neutral-800 hover:text-white hover:bg-primary-600 dark:text-neutral-100 dark:hover:text-white dark:hover:bg-primary-600 rounded-md transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 focus:ring-offset-white dark:focus:ring-offset-neutral-800"
              aria-label={isMobileMenuOpen ? 'Close menu' : 'Open menu'}
              aria-expanded={isMobileMenuOpen}
              aria-controls="mobile-menu"
            >
              <FontAwesomeIcon 
                icon={isMobileMenuOpen ? faTimes : faBars} 
                className="h-5 w-5"
              />
            </button>
          </div>
        </div>

        {/* Mobile Navigation Menu - Always available */}
        {isMobileMenuOpen && (
          <nav 
            id="mobile-menu"
            className="border-t border-neutral-200 dark:border-neutral-700 py-4 bg-white dark:bg-neutral-800"
            role="navigation"
            aria-label="Mobile navigation"
          >
            <div className="space-y-2">
              {navItems.map(item => (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={() => setIsMobileMenuOpen(false)}
                  className={`flex items-center space-x-3 px-4 py-3 text-base font-semibold rounded-md transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 focus:ring-offset-white dark:focus:ring-offset-neutral-800 ${
                    location.pathname === item.path
                      ? 'text-white bg-primary-600 shadow-md dark:text-white dark:bg-primary-600'
                      : 'text-neutral-800 hover:text-white hover:bg-primary-600 dark:text-neutral-100 dark:hover:text-white dark:hover:bg-primary-600'
                  }`}
                  aria-label={`Go to ${item.label} page`}
                  aria-current={location.pathname === item.path ? 'page' : undefined}
                >
                  <FontAwesomeIcon icon={item.icon} className="h-5 w-5" />
                  <span>{item.label}</span>
                </Link>
              ))}
            </div>
          </nav>
        )}
      </div>
    </header>
  );
}

export default Header;